import React, { useState, useEffect, useRef } from 'react';
import { CommonButton } from '@/components/ui/common-button';
import { CommonInput } from '@/components/ui/common-input';
import { CommonSelect } from '@/components/ui/common-select';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import masterDataService from '@/services/masterDataService';
import productService from '@/services/productService';
import tripService from '@/services/tripService';

const WHITELISTED_FIELDS = [
  { value: 'product_type_id', label: 'Product Type', type: 'select_product_type' },
  { value: 'product_quantity', label: 'Product Quantity', type: 'number' },
  { value: 'estimated_kms', label: 'Estimated Kms', type: 'number' },
  { value: 'invoice_number', label: 'Invoice Number', type: 'text', placeholder: 'Enter invoice number' },
  { value: 'dc_number', label: 'DC Number', type: 'text', placeholder: 'Enter DC number' },
  { value: 'eway_bill_number', label: 'E-way Bill Number', type: 'text', placeholder: 'Enter E-way bill number' },
  { value: 'driver_id', label: 'Driver', type: 'select_driver' },
  { value: 'vehicle_id', label: 'Vehicle', type: 'select_vehicle' },
  { value: 'rake_point_id', label: 'Rake Point', type: 'select_rake_point' },
  { value: 'rr_number', label: 'RR Number', type: 'text', placeholder: 'Enter RR number' },
  { value: 'rr_date', label: 'RR Date', type: 'datetime' },
  { value: 'billed_to_party', label: 'Billed To Party', type: 'text', placeholder: 'Enter billed to party' },
  { value: 'shipped_to_party', label: 'Shipped To Party', type: 'text', placeholder: 'Enter shipped to party' },
  { value: 'scheduled_start_time', label: 'Scheduled Start Time', type: 'datetime' },
  { value: 'scheduled_end_time', label: 'Scheduled End Time', type: 'datetime' }
];

// Lightweight reusable helper for scroll pagination + search in dropdowns
const usePaginatedOptions = (fetchFn, mapFn) => {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchTimeoutRef = useRef(null);
  const isFetchedRef = useRef(false);

  const fetchInitial = async () => {
    if (isFetchedRef.current || loading) return;
    try {
      setLoading(true);
      isFetchedRef.current = true;
      const res = await fetchFn(0, 10, '');
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setOptions(list.map(mapFn));
      setSkip(10);
      setHasMore(list.length === 10);
    } catch (e) {
      isFetchedRef.current = false;
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    try {
      setLoadingMore(true);
      const res = await fetchFn(skip, 10, search);
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      if (list.length > 0) {
        setOptions((prev) => {
          const ids = new Set(prev.map((p) => p.value));
          return [...prev, ...list.filter((item) => !ids.has(item.id)).map(mapFn)];
        });
        setSkip((prev) => prev + 10);
      }
      setHasMore(list.length === 10);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = (query) => {
    setSearch(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await fetchFn(0, 10, query);
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setOptions(list.map(mapFn));
        setSkip(10);
        setHasMore(list.length === 10);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  return { options, search, loading, loadingMore, hasMore, fetchInitial, loadMore, handleSearch };
};

const CreateTicketForm = ({ trip, onSubmit, onCancel }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [changes, setChanges] = useState([
    { field_name: '', new_value: '', temp_label: '' }
  ]);
  const [errors, setErrors] = useState({});

  const drivers = usePaginatedOptions(
    (skip, limit, search) => masterDataService.getDrivers(skip, limit, false, false, search),
    (d) => ({ value: d.id, label: `${d.name || d.user?.username || 'Driver'}${d.license_number ? ` (${d.license_number})` : ''}` })
  );

  const vehicles = usePaginatedOptions(
    (skip, limit, search) => masterDataService.getVehicles(skip, limit, false, false, search),
    (v) => ({ value: v.id, label: v.vehicle_number })
  );

  const products = usePaginatedOptions(
    (skip, limit) => productService.getProducts(skip, limit, false),
    (p) => ({ value: p.id, label: p.unit_of_measure ? `${p.name} (${p.unit_of_measure})` : p.name })
  );

  const [rakePointOptions, setRakePointOptions] = useState([]);
  const isRakeFetchedRef = useRef(false);

  useEffect(() => {
    changes.forEach((c) => {
      if (c.field_name === 'driver_id') drivers.fetchInitial();
      if (c.field_name === 'vehicle_id') vehicles.fetchInitial();
      if (c.field_name === 'product_type_id') products.fetchInitial();
      if (c.field_name === 'rake_point_id' && !isRakeFetchedRef.current) {
        isRakeFetchedRef.current = true;
        masterDataService.getRakePoints()
          .then((res) => {
            const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            setRakePointOptions(list.map((r) => ({ value: r.id, label: r.name })));
          })
          .catch(() => {
            isRakeFetchedRef.current = false;
          });
      }
    });
  }, [changes]);

  const handleAddRow = () => {
    setChanges(prev => [...prev, { field_name: '', new_value: '', temp_label: '' }]);
  };

  const handleRemoveRow = (index) => {
    if (changes.length > 1) {
      setChanges(prev => prev.filter((_, idx) => idx !== index));
    }
  };

  const handleFieldChange = (index, fieldName) => {
    const selectedField = WHITELISTED_FIELDS.find(f => f.value === fieldName);
    
    if (fieldName === 'driver_id') drivers.fetchInitial();
    if (fieldName === 'vehicle_id') vehicles.fetchInitial();
    if (fieldName === 'product_type_id') products.fetchInitial();
    if (fieldName === 'rake_point_id' && !isRakeFetchedRef.current) {
      isRakeFetchedRef.current = true;
      masterDataService.getRakePoints()
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
          setRakePointOptions(list.map((r) => ({ value: r.id, label: r.name })));
        })
        .catch(() => {
          isRakeFetchedRef.current = false;
        });
    }

    setChanges(prev => prev.map((row, idx) => {
      if (idx === index) {
        return {
          field_name: fieldName,
          new_value: '',
          temp_label: selectedField?.label || ''
        };
      }
      return row;
    }));
  };

  const handleValueChange = (index, val) => {
    setChanges(prev => prev.map((row, idx) => {
      if (idx === index) {
        return { ...row, new_value: val };
      }
      return row;
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!reason.trim()) {
      newErrors.reason = 'Reason for update is required';
    } else if (reason.trim().length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters long';
    }

    const rowErrors = [];
    let hasInvalidRow = false;

    changes.forEach((row, idx) => {
      const err = {};
      if (!row.field_name) {
        err.field_name = 'Field is required';
        hasInvalidRow = true;
      }
      if (row.new_value === undefined || row.new_value === '') {
        err.new_value = 'New value is required';
        hasInvalidRow = true;
      }
      if (Object.keys(err).length > 0) {
        rowErrors[idx] = err;
      }
    });

    if (hasInvalidRow) {
      newErrors.changes = rowErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      
      
      const formattedChanges = changes.map(row => {
        const item = {
          field_name: row.field_name,
          new_value: row.new_value
        };
        
        
        const meta = WHITELISTED_FIELDS.find(f => f.value === row.field_name);
        if (meta?.type === 'number') {
          item.new_value = parseFloat(row.new_value);
        }
        
        
        if (meta?.type === 'datetime') {
          item.new_value = new Date(row.new_value).toISOString();
        }

        return item;
      });

      
      await tripService.createEditRequest(trip.id, reason, formattedChanges);
      
      toast({
        title: 'Edit Request Submitted',
        description: 'Your trip update request has been sent for admin review.',
      });

      if (onSubmit) onSubmit();
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.message || 'Failed to submit edit request.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderValueInput = (row, index) => {
    const selectedField = WHITELISTED_FIELDS.find(f => f.value === row.field_name);
    if (!selectedField) return null;

    const rowError = errors.changes?.[index]?.new_value;

    switch (selectedField.type) {
      case 'select_product_type':
        return (
          <CommonSelect
            label="New Value"
            placeholder="Select new product type"
            options={products.options}
            value={row.new_value}
            onChange={(val) => handleValueChange(index, val)}
            required
            searchable
            error={rowError}
            loading={products.loading}
            hasMore={products.hasMore}
            loadingMore={products.loadingMore}
            onLoadMore={products.loadMore}
          />
        );
      case 'number':
        return (
          <CommonInput
            label="New Value"
            placeholder={row.field_name === 'estimated_kms' ? 'Enter estimated kms' : 'Enter quantity'}
            type="number"
            value={row.new_value}
            onChange={(e) => handleValueChange(index, e.target.value)}
            required
            error={rowError}
          />
        );
      case 'datetime':
        return (
          <CommonInput
            label="New Value"
            type="datetime-local"
            value={row.new_value}
            onChange={(e) => handleValueChange(index, e.target.value)}
            required
            error={rowError}
          />
        );
      case 'select_driver':
        return (
          <CommonSelect
            label="New Value"
            placeholder="Select new driver"
            options={drivers.options}
            value={row.new_value}
            onChange={(val) => handleValueChange(index, val)}
            required
            searchable
            error={rowError}
            loading={drivers.loading}
            searchValue={drivers.search}
            onSearchChange={drivers.handleSearch}
            onSearch={drivers.handleSearch}
            hasMore={drivers.hasMore}
            loadingMore={drivers.loadingMore}
            onLoadMore={drivers.loadMore}
          />
        );
      case 'select_vehicle':
        return (
          <CommonSelect
            label="New Value"
            placeholder="Select new vehicle"
            options={vehicles.options}
            value={row.new_value}
            onChange={(val) => handleValueChange(index, val)}
            required
            searchable
            error={rowError}
            loading={vehicles.loading}
            searchValue={vehicles.search}
            onSearchChange={vehicles.handleSearch}
            onSearch={vehicles.handleSearch}
            hasMore={vehicles.hasMore}
            loadingMore={vehicles.loadingMore}
            onLoadMore={vehicles.loadMore}
          />
        );
      case 'select_rake_point':
        return (
          <CommonSelect
            label="New Value"
            placeholder="Select new terminal"
            options={rakePointOptions}
            value={row.new_value}
            onChange={(val) => handleValueChange(index, val)}
            required
            searchable
            error={rowError}
          />
        );
      default:
        return (
          <CommonInput
            label="New Value"
            placeholder={selectedField.placeholder || "Enter new correct value"}
            value={row.new_value}
            onChange={(e) => handleValueChange(index, e.target.value)}
            required
            error={rowError}
          />
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        
        <div className="flex flex-col gap-1.5 w-full">
          <Label className="text-[13px] font-medium tracking-tight text-foreground/80">
            Reason for update (min 10 characters) <span className="text-destructive">*</span>
          </Label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Provide a detailed explanation for these corrections..."
            className={`w-full bg-background border ${errors.reason ? 'border-destructive focus-visible:ring-destructive/30' : 'border-input focus:border-primary'} rounded-lg p-3 text-sm min-h-[90px] outline-none text-foreground transition-all duration-200`}
            required
          />
          {errors.reason && (
            <span className="text-xs text-destructive">{errors.reason}</span>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-[13px] font-medium tracking-tight text-foreground/80">
              Fields to correct <span className="text-destructive">*</span>
            </Label>
            <CommonButton
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddRow}
            >
              Add Field
            </CommonButton>
          </div>

          {changes.map((row, idx) => (
            <div key={idx} className="flex flex-col md:flex-row md:items-start gap-3 p-3 bg-secondary/30 rounded-lg relative">
              <div className="flex-1">
                <CommonSelect
                  label="Select Field"
                  placeholder="Choose field"
                  options={WHITELISTED_FIELDS}
                  value={row.field_name}
                  onChange={(val) => handleFieldChange(idx, val)}
                  required
                  error={errors.changes?.[idx]?.field_name}
                />
              </div>

              {row.field_name && (
                <div className="flex-1">
                  {renderValueInput(row, idx)}
                </div>
              )}

              {changes.length > 1 && (
                <div className="flex items-center pt-6">
                  <button
                    type="button"
                    onClick={() => handleRemoveRow(idx)}
                    className="px-3 py-2 text-xs font-medium text-red-500 hover:bg-red-500/10 rounded-md transition-colors h-10 flex items-center justify-center"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <CommonButton type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </CommonButton>
        <CommonButton type="submit" loading={loading}>
          Submit Edit Request
        </CommonButton>
      </div>
    </form>
  );
};

export default CreateTicketForm;
